import React, { useLayoutEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Loader from '../../../general/Loader';
import { className } from '../../../../lib/className';
import { useI18n } from '../../../../../i18n';
import DatetimeField from '../../../general/datetime/DatetimeField';
import Address from '../../../general/location/Address';
import { formatter } from '../../../../misc/datetime';
import { scrollReportItemIntoView } from '../../../../misc/misc';
import ScrollList from '../../../general/list/ScrollList';
import ReportSummary from './ReportSummary';
import ReportHeader from './ReportHeader';
import { actions } from '../../../../redux/api/reports';
import CheckLocationMap from './CheckLocationMap';
import SwitchMapMenuItem from './SwitchMapMenuItem';
import NoDataView from '../../../general/view/NoDataView';
import './temperatureHistory.scss';

function HeaderListItem() {
	const { f } = useI18n();
	return (
		<>
			<div className="header-item"><span className="capitalize">{f('item No.')}</span></div>
			<div className="header-item"><span className="capitalize">{f('start time')}</span></div>
			<div className="header-item"><span className="capitalize">{f('end time')}</span></div>
			<div className="header-item"><span className="capitalize">{f('duration')}</span></div>
			<div className="header-item"><span className="capitalize">{f('location')}</span></div>
			<div className="header-item rows">
				<div><span className="capitalize">{f('temperature')} 1</span></div>
				<div>
					<div><span className="capitalize">{f('minimum')}, &deg;C</span></div>
					<div><span className="capitalize">{f('maximum')}, &deg;C</span></div>
					<div><span className="capitalize">{f('average')}, &deg;C</span></div>
					<div><span className="capitalize">{f('quantity')}</span></div>
				</div>
			</div>
			<div className="header-item rows">
				<div><span className="capitalize">{f('temperature')} 2</span></div>
				<div>
					<div><span className="capitalize">{f('minimum')}, &deg;C</span></div>
					<div><span className="capitalize">{f('maximum')}, &deg;C</span></div>
					<div><span className="capitalize">{f('average')}, &deg;C</span></div>
					<div><span className="capitalize">{f('quantity')}</span></div>
				</div>
			</div>
		</>
	)
}

function ReportListItem(props) {
	const onClick = () => {
		props.onClick(props.temperature, props.index);
	}

	const _className = className('clickable', { 'selected': props.selected });

	const t1 = props.temperature.temperature1;
	const t2 = props.temperature.temperature2;
	return (
		<>
			<div ref={props.customRef} className={_className} onClick={onClick}>{props.index}</div>
			<div className={_className} onClick={onClick}><DatetimeField datetime={props.temperature.since} /></div>
			<div className={_className} onClick={onClick}>
				{props.temperature.until && <DatetimeField datetime={props.temperature.until} now={props.temperature.since} />}
			</div>
			<div className={_className} onClick={onClick}>
				{props.temperature.until && formatter.duration(props.temperature.since, props.temperature.until)}
			</div>
			<div className={_className} onClick={onClick}><Address message={props.temperature} full /></div>
			<div className={"temperature-group " + _className} onClick={onClick}>
				<div>{t1 && t1.minimum || "-"}</div>
				<div>{t1 && t1.maximum || "-"}</div>
				<div>{t1 && t1.average || "-"}</div>
				<div>{t1 && t1.quantity || "-"}</div>
			</div>
			<div className={"temperature-group " + _className} onClick={onClick}>
				<div>{t2 && t2.minimum || "-"}</div>
				<div>{t2 && t2.maximum || "-"}</div>
				<div>{t2 && t2.average || "-"}</div>
				<div>{t2 && t2.quantity || "-"}</div>
			</div>
		</>
	)
}

function EodListItem() {
	const { f } = useI18n();
	return <div className="indicator"><span className="capitalize">{f('you have reached the end')}</span></div>;
}

// -----------------------------------------------------------------

function TemperatureHistory(props) {
	const selectedItemBox = useRef();
	const [displayMap, setDisplayMap] = useState(false);
	const [selectedTemperature, setSelectedTemperature] = useState({}); // { [index]: temperature }
	const { f } = useI18n();
	const uri = props.state.uri;
	const device = props.deviceMap[uri];

	useLayoutEffect(() => {
		if (selectedItemBox.current) {
			scrollReportItemIntoView(selectedItemBox.current);
		}
	}, [selectedTemperature]);

	const loadNext = () => {
		const parameters = props.state.parameters;
		props.dispatch(actions.temperatureHistory.request({ uri, parameters }));
	}

	const onItemClick = (temperature, index) => {
		setSelectedTemperature({ [index]: temperature });
		if (!displayMap) setDisplayMap(true);
	}

	let content = null;
	let mapBox = null;
	let canExport = false;
	if (props.state.list != null) {
		if (!props.state.list || props.state.list.length == 0) {
			content = <NoDataView message={f('no data for such criteria')} />;
		} else {
			canExport = true;
			let items = props.state.list.map((temperature, at) => {
				const index = at + 1;
				const isSelected = Object.keys(selectedTemperature)[0] == index;
				return (
					<ReportListItem
						key={at}
						onClick={onItemClick}
						selected={isSelected}
						customRef={isSelected ? selectedItemBox : undefined}
						temperature={temperature}
						index={index}
					/>
				);
			});
			items.unshift(<HeaderListItem key={1000000} />);
			content = (<ScrollList
				pending={props.state.pending}
				next={loadNext}
				isEod={!props.state.hasMore}
				eodItem={EodListItem}
			>
				{items}
			</ScrollList>);
			if (displayMap) {
				const onClick = () => {
					setSelectedTemperature({ ...selectedTemperature });
				}
				mapBox = (<CheckLocationMap onClick={onClick} locations={selectedTemperature} device={device} />);
			}
		}
	} else {
		content = (
			<div className="center">
				{
					props.state.error
						? <span className="error">{props.state.error}</span>
						: <Loader size={Loader.Size.MD} />
				}
			</div>
		);
	}

	const onMapSwitch = () => {
		setDisplayMap(!displayMap);
	}

	const exportReport = () => {
		props.dispatch(actions.temperatureHistory.export({ uri }));
		props.history.goBack();
	}

	const timeRange = props.state.parameters.timeRange;
	return (
		<div className="report temperature-history">
			<ReportHeader
				title={f('temperature history report')}
				canExport={canExport}
				onExport={exportReport}
				items={<SwitchMapMenuItem disabled={!canExport} active={displayMap} onClick={onMapSwitch} />}
			/>
			<ReportSummary
				since={timeRange.since}
				until={timeRange.until}
				devices={[device]}
				generatedAt={props.state.at}
			/>
			<div className={className("content", { 'with-map': displayMap })}>
				{content}
				{mapBox}
			</div>
		</div>
	)
}

export default withRouter(connect(state => {
	return ({
		state: state.reports.temperatureHistory,
		deviceMap: state.devices.map
	})
})(TemperatureHistory));
